import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { Heart, ShoppingCart, X } from 'lucide-react';
import { APP_NAME, ROUTE_NAMES } from '@tsgecart/config';
import { useCartItemCount } from '../../cart/hooks/use-cart.js';
import { useWishlistItemCount } from '../../wishlist/hooks/use-wishlist.js';
import { useAppUser } from '../../../../lib/auth/use-app-user.js';
import { SearchBar } from './search-bar.js';
import { CategoryNav } from './category-nav.js';
import { EarnWithFunButton } from '../../rewards/components/earn-with-fun-button.js';

export interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

const ACCOUNT_LINKS = [
  { label: 'Profile', to: ROUTE_NAMES.accountProfile },
  { label: 'My Orders', to: ROUTE_NAMES.accountOrders },
  { label: 'Addresses', to: ROUTE_NAMES.accountAddresses },
  { label: 'Rewards', to: ROUTE_NAMES.accountRewards },
  { label: 'Notifications', to: ROUTE_NAMES.accountNotifications },
] as const;

/**
 * Hamburger-triggered accessible drawer -- focus-trapped, closes on
 * Escape and on backdrop click, same base pattern as
 * `AdminMobileNavigation` plus an explicit focus trap since this menu
 * carries interactive search/nav content a customer relies on more than
 * the admin rail does.
 */
export function MobileMenu({ open, onClose }: MobileMenuProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const cartCount = useCartItemCount();
  const wishlistCount = useWishlistItemCount();
  const { data: appUser } = useAppUser();
  const canAccessAdmin = appUser?.roles.some((role) => role === 'ADMIN' || role === 'STAFF') ?? false;

  useEffect(() => {
    if (!open) return;

    const container = panelRef.current;
    const focusables = container?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input');
    focusables?.[0]?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !container) return;
      const items = Array.from(container.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input'));
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-40 flex lg:hidden">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${APP_NAME} navigation`}
        className="relative flex h-full w-80 max-w-[88vw] flex-col gap-5 overflow-y-auto bg-white p-4 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <Link to={ROUTE_NAMES.home} onClick={onClose} className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="bg-brand text-brand-foreground ring-1 ring-black/5 flex h-9 w-9 items-center justify-center rounded-xl text-base font-black leading-none shadow-sm"
            >
              T
            </span>
            <span className="text-lg font-extrabold leading-none tracking-tight text-slate-900">{APP_NAME}</span>
          </Link>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="focus-visible:ring-brand flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <SearchBar onSubmitted={onClose} />

        <nav aria-label="Primary" className="flex flex-col gap-1 border-b border-gray-100 pb-4 text-sm font-medium">
          <Link to={ROUTE_NAMES.home} onClick={onClose} className="rounded-lg px-2 py-2 text-gray-700 hover:bg-gray-50">
            Home
          </Link>
          <Link to={ROUTE_NAMES.shop} onClick={onClose} className="rounded-lg px-2 py-2 text-gray-700 hover:bg-gray-50">
            Shop
          </Link>
          <Link to={ROUTE_NAMES.about} onClick={onClose} className="rounded-lg px-2 py-2 text-gray-700 hover:bg-gray-50">
            About
          </Link>
          <Link to={ROUTE_NAMES.contact} onClick={onClose} className="rounded-lg px-2 py-2 text-gray-700 hover:bg-gray-50">
            Contact
          </Link>
          <div className="pt-1">
            <EarnWithFunButton onClick={onClose} fullWidth />
          </div>
        </nav>

        <div className="border-b border-gray-100 pb-4">
          <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Categories</p>
          <CategoryNav variant="mobile" onNavigate={onClose} />
        </div>

        <SignedIn>
          <div className="flex items-center gap-4 border-b border-gray-100 pb-4">
            <Link
              to={ROUTE_NAMES.cart}
              onClick={onClose}
              className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ShoppingCart className="h-4 w-4" aria-hidden="true" />
              Cart{cartCount > 0 ? ` (${cartCount})` : ''}
            </Link>
            <Link
              to={ROUTE_NAMES.accountWishlist}
              onClick={onClose}
              className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Heart className="h-4 w-4" aria-hidden="true" />
              Wishlist{wishlistCount > 0 ? ` (${wishlistCount})` : ''}
            </Link>
          </div>

          <nav aria-label="Account" className="flex flex-col gap-1 text-sm font-medium">
            {ACCOUNT_LINKS.map((link) => (
              <Link key={link.to} to={link.to} onClick={onClose} className="rounded-lg px-2 py-2 text-gray-700 hover:bg-gray-50">
                {link.label}
              </Link>
            ))}
            {canAccessAdmin && (
              <Link
                to={ROUTE_NAMES.adminDashboard}
                onClick={onClose}
                className="rounded-lg px-2 py-2 text-gray-700 hover:bg-gray-50"
              >
                Admin Dashboard
              </Link>
            )}
          </nav>
        </SignedIn>

        <SignedOut>
          <div className="flex flex-col gap-2">
            <Link
              to={ROUTE_NAMES.signUp}
              onClick={onClose}
              className="bg-brand text-brand-foreground hover:bg-brand/90 rounded-full px-4 py-2 text-center text-sm font-semibold"
            >
              Sign up
            </Link>
            <Link
              to={ROUTE_NAMES.signIn}
              onClick={onClose}
              className="rounded-full border border-gray-200 px-4 py-2 text-center text-sm font-semibold text-slate-700 hover:bg-gray-50"
            >
              Log in
            </Link>
          </div>
        </SignedOut>
      </div>
    </div>,
    document.body,
  );
}
